import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { DownloadSimple } from '@phosphor-icons/react';
import api from '../services/api';
import Logo from '../components/Logo';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const monthNames = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const currencyMap = {
  BRL: { symbol: 'R$', locale: 'pt-BR', code: 'BRL' },
  USD: { symbol: '$', locale: 'en-US', code: 'USD' },
  EUR: { symbol: '\u20ac', locale: 'de-DE', code: 'EUR' },
};

function fmt(value, currency) {
  const c = currencyMap[currency] || currencyMap.BRL;
  return parseFloat(value).toLocaleString(c.locale, { style: 'currency', currency: c.code });
}

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/share/${token}`)
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <svg className="w-6 h-6 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4">
        <Logo />
        <p className="text-slate-400 text-sm mt-8">Link expirado ou invalido.</p>
      </div>
    );
  }

  const { expenses, total, userName, currency, year, month } = data;

  function exportPDF() {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    doc.text('Desp', 14, 18);
    const w = doc.getTextWidth('Desp');
    doc.setTextColor(37, 99, 235);
    doc.text('Easy', 14 + w, 18);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184);
    doc.text(`Relatorio de ${userName} - ${monthNames[month - 1]} ${year}`, 14, 24);

    autoTable(doc, {
      startY: 32,
      head: [['Descricao', 'Categoria', 'Data', 'Valor']],
      body: expenses.map(e => [
        e.description,
        e.category,
        new Date(e.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
        fmt(e.amount, currency),
      ]),
      foot: [['Total', '', '', fmt(total, currency)]],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
      footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold' },
    });

    doc.save(`despeasy-${monthNames[month - 1].toLowerCase()}-${year}.pdf`);
  }

  function exportCSV() {
    const rows = expenses.map(e => [
      e.description,
      parseFloat(e.amount).toFixed(2),
      e.category,
      new Date(e.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
    ]);
    const csv = [['Descricao', 'Valor', 'Categoria', 'Data'], ...rows, ['Total', total.toFixed(2), '', '']]
      .map(row => row.join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `despeasy-${monthNames[month - 1].toLowerCase()}-${year}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Categorias
  const cats = {};
  expenses.forEach(e => {
    const cat = e.category || 'Geral';
    cats[cat] = (cats[cat] || 0) + parseFloat(e.amount);
  });
  const sorted = Object.entries(cats).sort(([,a], [,b]) => b - a);
  const colors = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
  const maxVal = sorted.length > 0 ? Math.max(...sorted.map(([,v]) => v)) : 1;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo />
          <span className="text-xs text-slate-400">Relatorio compartilhado</span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">{monthNames[month - 1]} {year}</h1>
            <p className="text-slate-400 text-sm mt-1">Relatorio de {userName}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPDF}
              className="bg-blue-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl hover:bg-blue-700 transition text-sm font-medium shadow-sm flex items-center gap-1.5 sm:gap-2"
            >
              <DownloadSimple size={16} />
              PDF
            </button>
            <button
              onClick={exportCSV}
              className="border border-slate-200 text-slate-600 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl hover:bg-slate-50 transition text-sm font-medium shadow-sm flex items-center gap-1.5 sm:gap-2"
            >
              <DownloadSimple size={16} />
              CSV
            </button>
          </div>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
            <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Total</p>
            <p className="text-lg sm:text-2xl font-bold text-slate-800 truncate">{fmt(total, currency)}</p>
          </div>
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
            <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Despesas</p>
            <p className="text-lg sm:text-2xl font-bold text-slate-800">{expenses.length}</p>
          </div>
        </div>

        {/* Barras por categoria */}
        {sorted.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6 mb-6">
            <h3 className="font-semibold text-slate-800 mb-4">Por categoria</h3>
            <div className="space-y-3">
              {sorted.map(([cat, val], i) => (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-600">{cat}</span>
                    <span className="text-xs font-medium text-slate-800">{fmt(val, currency)}</span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${(val / maxVal) * 100}%`, backgroundColor: colors[i % colors.length] }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lista */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-3 sm:px-6 py-3">Descricao</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-3 sm:px-6 py-3">Categoria</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-3 sm:px-6 py-3">Data</th>
                  <th className="text-right text-xs font-medium text-slate-400 uppercase tracking-wider px-3 sm:px-6 py-3">Valor</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense, index) => (
                  <tr key={expense.id || index} className={`${index < expenses.length - 1 ? 'border-b border-slate-50' : ''}`}>
                    <td className="px-3 sm:px-6 py-3.5 text-sm font-medium text-slate-800 max-w-[120px] sm:max-w-none truncate">{expense.description}</td>
                    <td className="px-3 sm:px-6 py-3.5">
                      <span className="text-[11px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md font-medium whitespace-nowrap">{expense.category}</span>
                    </td>
                    <td className="px-3 sm:px-6 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                      {new Date(expense.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                    </td>
                    <td className="px-3 sm:px-6 py-3.5 text-sm font-semibold text-slate-800 text-right whitespace-nowrap">
                      {fmt(expense.amount, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-200 bg-slate-50/50">
                  <td colSpan={3} className="px-3 sm:px-6 py-3.5 text-sm font-semibold text-slate-800">Total</td>
                  <td className="px-3 sm:px-6 py-3.5 text-sm font-bold text-slate-800 text-right whitespace-nowrap">{fmt(total, currency)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
